import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { qk } from './queryKeys';
import type {
  CycleReviewTargetDto, DisputeDto, Guid, OpenDisputeRequest, ParticipantReviewsDto, ReviewDto, ReviewListDto, WriteReviewRequest,
} from './types';

export const feedbackApi = {
  cycleReviews: (cycleId: Guid) => api<CycleReviewTargetDto[]>(`/api/cycles/${cycleId}/reviews`),
  reviewParticipant: (participantId: Guid, body: WriteReviewRequest) => api<ReviewDto>(`/api/participants/${participantId}/review`, { method: 'PUT', body }),
  participantReviews: (participantId: Guid) => api<ParticipantReviewsDto>(`/api/participants/${participantId}/reviews`),
  reviewCustomer: (participantId: Guid, body: WriteReviewRequest) =>
    api<ReviewDto>(`/api/participants/${participantId}/customer-review`, { method: 'PUT', body }),
  testerReviews: (userId: Guid) => api<ReviewListDto>(`/api/testers/${userId}/reviews`),
  teamReviews: (teamId: Guid) => api<ReviewListDto>(`/api/teams/${teamId}/reviews`),
  orgReviews: (orgId: Guid) => api<ReviewListDto>(`/api/organizations/${orgId}/reviews`),
  openDispute: (body: OpenDisputeRequest) => api<DisputeDto>('/api/disputes', { method: 'POST', body }),
  myDisputes: () => api<DisputeDto[]>('/api/my/disputes'),
  orgDisputes: (orgId: Guid) => api<DisputeDto[]>(`/api/organizations/${orgId}/disputes`),
  respond: (id: Guid, body: { reverse: boolean; response: string | null }) => api<DisputeDto>(`/api/disputes/${id}/respond`, { method: 'POST', body }),
  withdraw: (id: Guid) => api<void>(`/api/disputes/${id}/withdraw`, { method: 'POST' }),
  adminDisputes: () => api<DisputeDto[]>('/api/admin/disputes'),
  decide: (id: Guid, body: { overturn: boolean; note: string }) => api<DisputeDto>(`/api/admin/disputes/${id}/decision`, { method: 'POST', body }),
};

export function useCycleReviews(cycleId: Guid | undefined, enabled = true) {
  return useQuery({ queryKey: qk.feedback.cycle(cycleId ?? ''), queryFn: () => feedbackApi.cycleReviews(cycleId!), enabled: !!cycleId && enabled });
}

export function useParticipantReviews(participantId: Guid | null | undefined) {
  return useQuery({ queryKey: qk.feedback.participant(participantId ?? ''), queryFn: () => feedbackApi.participantReviews(participantId!), enabled: !!participantId });
}

export function useTesterReviews(userId: Guid | undefined) {
  return useQuery({ queryKey: qk.feedback.tester(userId ?? ''), queryFn: () => feedbackApi.testerReviews(userId!), enabled: !!userId });
}

export function useTeamReviews(teamId: Guid | undefined) {
  return useQuery({ queryKey: qk.feedback.team(teamId ?? ''), queryFn: () => feedbackApi.teamReviews(teamId!), enabled: !!teamId });
}

export function useOrgReviews(orgId: Guid | undefined) {
  return useQuery({ queryKey: qk.feedback.org(orgId ?? ''), queryFn: () => feedbackApi.orgReviews(orgId!), enabled: !!orgId });
}

/** Writing a review (either direction) refreshes every rating on screen. */
export function useWriteReview(direction: 'participant' | 'customer') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ participantId, body }: { participantId: Guid; body: WriteReviewRequest }) =>
      direction === 'participant' ? feedbackApi.reviewParticipant(participantId, body) : feedbackApi.reviewCustomer(participantId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.feedback.all });
      void qc.invalidateQueries({ queryKey: qk.market.all });
    },
  });
}

export function useMyDisputes(enabled = true) {
  return useQuery({ queryKey: qk.feedback.myDisputes, queryFn: feedbackApi.myDisputes, enabled });
}

export function useOrgDisputes(orgId: Guid | undefined) {
  return useQuery({ queryKey: qk.feedback.orgDisputes(orgId ?? ''), queryFn: () => feedbackApi.orgDisputes(orgId!), enabled: !!orgId });
}

export function useAdminDisputes() {
  return useQuery({ queryKey: qk.feedback.adminDisputes, queryFn: feedbackApi.adminDisputes });
}

/** Any dispute change also changes runs, bugs, earnings and reputation. */
function useDisputeMutation<TVars, TResult>(fn: (v: TVars) => Promise<TResult>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const key of [qk.feedback.all, qk.market.all, qk.my.all, qk.executions.all, qk.bugs.all, qk.reviewQueue.all, ['cycles']])
        void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export const useOpenDispute = () => useDisputeMutation(feedbackApi.openDispute);
export const useRespondDispute = () =>
  useDisputeMutation(({ id, reverse, response }: { id: Guid; reverse: boolean; response: string | null }) => feedbackApi.respond(id, { reverse, response }));
export const useWithdrawDispute = () => useDisputeMutation(feedbackApi.withdraw);
export const useDecideDispute = () =>
  useDisputeMutation(({ id, overturn, note }: { id: Guid; overturn: boolean; note: string }) => feedbackApi.decide(id, { overturn, note }));
